import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import { formatLotNumber } from '../../versa/misc.js';
import filter, { resolveFilterLabelsToIds, translate } from '../../versa/filter.js';
import grid from '../../versa/grid.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import { notify, ask } from '../../versa/msgbox.js';
import { splitIdsByCompany, resolvePrinterDeviceId } from '../../versa/misc.js';
import { getTopIndex } from '../../versa/record.js';
import pdf from '../../versa/pdf.js';
import view from '../views/stocks.js';
import form from '../forms/stocks.js';
import move_stock_form from '../forms/stocks-move-stock.js';
import block_stock_form from '../forms/stocks-block-stock.js';
import select_printer_form from '../forms/stocks-select-printer.js';
import report_modal_form from '../forms/stocks-report-modal.js';
import export_csv_form from '../forms/stocks-export-csv.js';
import output_purchase_form from '../forms/output-purchase-report.js';
import sm_lot_meta_form from '../forms/stock-management-lot-meta.js';
import sm_packaging_form from '../forms/stock-management-packaging.js';
import sm_bulk_target_form from '../forms/stock-management-bulk-target.js';
import sm_reason_form from '../forms/stock-management-reason.js';
import sm_split_form from '../forms/stock-management-split.js';
import sm_unit_form from '../forms/stock-management-unit.js';
import { resolveCompanyId, resolveWarehouseId, resolveLocationIdForMove } from '../utils/location-search.js';
import { manualMoveReservedError, formatBlockedMoveWarning } from '../utils/stock-move-confirm.js';
import { reasonFromMovementKind, syncBlockReasonFromKind } from '../utils/movement-kind-reason.js';
import * as types from '../types.js';

function downloadCsv(base64Content, filename) {
	const bin = atob(base64Content);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: 'text/csv;charset=utf-8' });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename || 'relatorio.csv';
	a.click();
	URL.revokeObjectURL(a.href);
}

/** Evita duplo clique em «Sim» (bloquear/desbloquear/mover em lote) disparando 2× a API. */
let _smBulkActionInFlight = false;

const _PKG_GENERIC_TOKENS = new Set([ 'teste', 'test', 'ajuste', 'correção', 'correcao', 'corrigir', 'ok', 'n/a', 'na', 'xxx' ]);

/**
 * Valida justificativa da correção de embalagem (espelha POP / API).
 * @param {string} reason
 * @returns {string|null} mensagem de erro ou null se ok
 */
function sm_validate_packaging_reason(reason)
{
	const text = String(reason || '').trim();
	if (text.length < 10)
		return 'Informe a justificativa da correção de embalagem (mínimo 10 caracteres).';
	const words = text.split(/\s+/).filter(Boolean);
	if (words.length < 2)
		return 'Justificativa muito curta: descreva o motivo da correção com mais detalhe.';
	const low = text.toLowerCase();
	if (_PKG_GENERIC_TOKENS.has(low))
		return 'Justificativa genérica não aceita: descreva o motivo operacional da correção.';
	if (new Set(text).size <= 2)
		return 'Justificativa genérica não aceita: descreva o motivo operacional da correção.';
	if (words.every((w) => _PKG_GENERIC_TOKENS.has(w.toLowerCase().replace(/[.,;:!?]+$/g, ''))))
		return 'Justificativa genérica não aceita: descreva o motivo operacional da correção.';
	return null;
}

/** Agrega contexto de conferência para o modal de correção de embalagem. */
function sm_packaging_context(rows)
{
	let total = 0;
	let avail = 0;
	let res = 0;
	let block = 0;
	const lots = new Map();
	const locs = new Set();
	for (const s of rows)
	{
		const a = Number(s.quantity_available) || 0;
		const r = Number(s.quantity_reserved) || 0;
		const b = Number(s.quantity_unsettled) || 0;
		total += a + r + b;
		avail += a;
		res += r;
		block += b;
		const k = `${s.lot_company_id}|${s.lot_id}`;
		if (!lots.has(k))
			lots.set(k, s);
		if (s.warehouse_id != null && s.location_id != null)
			locs.add(`${s.warehouse_id}-${s.location_id}`);
	}
	return { first: rows[0], lots: [ ...lots.values() ], total, avail, res, block, locs: [ ...locs ] };
}

async function sm_load_selected_stocks(crud)
{
	const raw = crud.getSelection();
	if (!raw?.length)
	{
		notify('Marque as linhas desejadas na grade (checkbox). A operação não usa a página inteira.', 'Atenção');
		return null;
	}
	const out = [];
	const seen = new Set();
	for (const entry of raw)
	{
		let row = entry;
		if (typeof row === 'string')
		{
			try { row = JSON.parse(row); }
			catch (_) { continue; }
		}
		const company_id = Array.isArray(row) ? row[0] : row?.company_id;
		const stock_id = Array.isArray(row) ? row[1] : row?.id;
		if (company_id == null || stock_id == null)
			continue;
		const k = `${company_id}::${stock_id}`;
		if (seen.has(k))
			continue;
		seen.add(k);
		const s = await api.call(api.select_stock, company_id, stock_id);
		if (s)
			out.push(s);
	}
	return out.length ? out : null;
}

/** Rótulo operacional: SKU + lote fab. + posição (sem lot_id interno). */
function sm_stock_line_label(r)
{
	const sku = r?.sku ?? '';
	const lot = formatLotNumber(r?.lot_number) || r?.lot_number || '—';
	const pos = r?.location_address || `${r?.warehouse_id ?? '?'}-${r?.location_id ?? '?'}`;
	return `SKU ${sku} · Lote ${lot} · Posição ${pos}`;
}

function sm_lot_group_label(g)
{
	const sku = g?.sku ?? '';
	const lot = formatLotNumber(g?.lot_number) || g?.lot_number || '—';
	return `SKU ${sku} · Lote ${lot}`;
}

/** Posição destino: id numérico ou endereço digitado (busca case-sensitive na API). */
async function sm_resolve_target_location_id(data)
{
	let locId = Number(data.target_location_id);
	if (Number.isInteger(locId) && locId >= 1)
		return locId;
	const companyId = await resolveCompanyId(data.target_warehouse_company_id)
		|| data.target_warehouse_company_id;
	const wh = await resolveWarehouseId(companyId, data.target_warehouse_id);
	locId = await resolveLocationIdForMove(companyId, wh, data.target_location_id);
	return Number.isInteger(locId) && locId >= 1 ? locId : NaN;
}

const stock_management = function()
{

	let sm_crud = crud.create({
		default_columns: '1,2,12,13,15,16,17,18,19,20,21,22,24,26,25,28,27,29,30,3,4,5,6,7,8,9,37,38,39,10',
		insertRecord() {
			// Tela de estoque é somente consulta; criação/alteração é feita por entradas, movimentação e inventário
		},
		/**
		 * Operações exigem recorte definido: expressão de filtro da API, critérios no formulário,
		 * ou grade já carregada após «Aplicar» (evita falso negativo quando translate() não reproduz o critério).
		 */
		smHasActiveFilter()
		{
			const data = this.filter || {};
			if (translate(data, null, this.fieldspecs))
				return true;
			const top = getTopIndex(data) + 1;
			for (let i = 0; i < top; i++)
			{
				const f = data[`field-${i}`];
				const v = data[`value-${i}`];
				if (f && v != null && String(v).trim() !== '')
					return true;
			}
			return !!(this.filterExplicitlyApplied && this.record_count > 0);
		},
		smNotifyFilterRequired()
		{
			notify('As ações de Operações só podem ser usadas depois de Filtrar e Aplicar (com ou sem critérios). Trabalhe sempre sobre o recorte visível na grade.', 'Atenção');
		},
		/**
		 * @param {string} title
		 * @param {string} message
		 * @param {() => void | Promise<void>} fn
		 */
		smWithFilterAndConfirm(title, message, fn)
		{
			if (!this.smHasActiveFilter())
			{
				this.smNotifyFilterRequired();
				return;
			}
			const self = this;
			ask(message, title, (key) =>
			{
				if (key !== 'Y')
					return;
				Promise.resolve(fn.call(self)).catch((e) => console.error(e));
			});
		},
		async run(module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports, options = {}) {
			// Grade: alinhar colunas/checkboxes (v2=fabricante; v3=ordem/índices + coluna cód. cliente).
			try {
				const raw = localStorage.getItem('stock-management.setup');
				if (raw) {
					const s = JSON.parse(raw);
					let touch = false;
					if (!s.sm_stocks_grid_v2) {
						s.columns = this.default_columns;
						s.sm_stocks_grid_v2 = true;
						touch = true;
					}
				if (!s.sm_stocks_grid_v3) {
					s.columns = this.default_columns;
					s.sm_stocks_grid_v3 = true;
					touch = true;
				}
			if (!s.sm_stocks_grid_v4_classes) {
				s.sm_stocks_grid_v4_classes = true;
				touch = true;
			}
			if (!s.sm_stocks_grid_v5_categoria) {
				s.sm_stocks_grid_v5_categoria = true;
				touch = true;
			}
			if (!s.sm_stocks_grid_v6_values) {
				s.columns = this.default_columns;
				s.sm_stocks_grid_v6_values = true;
				touch = true;
			}
			if (touch)
					localStorage.setItem('stock-management.setup', JSON.stringify(s));
				}
			} catch (_) { /* ignore */ }
			await Object.getPrototypeOf(this).run.call(this, module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports, { ...options, skipInitialLoad: true });
			// Ocultar Inserir (tela somente consulta)
			const insertLi = this.target_node?.querySelector?.('li[hash="#insert"]');
			if (insertLi) insertLi.style.display = 'none';
			// #MT-FD0B172 — Configurar: colunas/ordenação já via applySetup.
			// "Preservar grade ao reabrir" (keep_filter=1) → recarrega grade SEM critérios do Filtrar.
			this.filter = {};
			const setup = this.loadSetup?.() || {};
			// Limpa snapshot antigo de critérios do Filtrar (não deve persistir nesta tela).
			if (setup.filter != null || setup.filter_applied != null)
			{
				delete setup.filter;
				delete setup.filter_applied;
				try { localStorage.setItem('stock-management.setup', JSON.stringify(setup)); } catch (_) { /* ignore */ }
			}
			if (setup.keep_filter === 1 || setup.keep_filter === true || setup.keep_filter === '1')
			{
				this.filterExplicitlyApplied = true;
				await this.loadPage(1);
				return;
			}
			const grid_node = this.target_node?.querySelector?.('#grid');
			if (grid_node) {
				grid_node.innerHTML = '<p class="mtm" style="color:var(--color-muted)">Clique em <strong>Filtrar</strong> e depois <strong>Aplicar</strong> para carregar a grade (busca completa sem critérios ou refine com filtros). <strong>Operações</strong> (menu superior) só funcionam após aplicar e pedem confirmação antes de executar.</p>';
			}
			const page = this.target_node?.querySelector?.('#page');
			if (page)
				page.innerText = '0/0';
		},
		async resolveFilterData(raw)
		{
			return await resolveFilterLabelsToIds(raw || this.filter || {}, this.fieldspecs);
		},
		normalizeFilterData(raw)
		{
			const out = { ...(raw || {}) };
			Object.keys(out).forEach((k) =>
			{
				const v = out[k];
				if (v === '-1' || v === -1 || v == null)
					out[k] = '';
			});
			return out;
		},
		/** Escopo para GET /locations — resolve rótulo de Empresa (tenant) para UUID quando necessário. */
		async extractLocationScopeFilterRaw(raw)
		{
			const parts = [];
			const data = raw || {};
			const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
			let companies = null;
			const resolveCompanyId = async (v) =>
			{
				if (UUID_REGEX.test(v))
					return v;
				companies = companies ?? await db.list('companies').catch(() => []);
				const c = companies.find(x =>
					(x.nick || '') === v || String(x.nick || '') === v
					|| (x.name || '') === v || String(x.name || '') === v);
				const id = c?.id;
				return id && UUID_REGEX.test(String(id)) ? String(id) : null;
			};
			for (let i = 0; i < 10; i++)
			{
				const f = data[`field-${i}`];
				let v = data[`value-${i}`];
				if (v == null || v === '' || v === '-1' || v === -1)
					continue;
				v = String(v).trim();
				if (f === 'company_id' && v)
				{
					const cid = await resolveCompanyId(v);
					if (cid)
						parts.push(`company_id*eq*${cid}`);
				}
				else if (f === 'company_id')
					continue;
				else if (f === 'warehouse_id' && /^\d+$/.test(v))
					parts.push(`warehouse_id*eq*${v}`);
				else if (f === 'location_id' && v)
				{
					const m = v.match(/^(\d+):(\d+)$/);
					if (m)
					{
						parts.push(`warehouse_id*eq*${m[1]}`);
						parts.push(`location_id*eq*${m[2]}`);
					}
					else if (/^\d+$/.test(v))
						parts.push(`location_id*eq*${v}`);
				}
			}
			return parts.length ? parts.join(',') : null;
		},
		async loadScopedLocations(raw)
		{
			const scopeFilter = await this.extractLocationScopeFilterRaw(raw);
			if (!scopeFilter)
				return;
			const params = new URLSearchParams();
			params.append('filter', scopeFilter);
			params.append('sort', '4');
			params.append('limit', '0');
			const data = await api.call(api.list_locations, '?' + params.toString());
			await db.set('locations', Array.isArray(data) ? data : []);
			return data;
		},
		async loadBaseData()
		{
			const q = this.queryAll();
			await Promise.all([
			api.call(api.list_companies, q).then((data) => db.set('companies', data)).catch(error => notify(error)),
			api.call(api.list_warehouses, q).then((data) => db.set('warehouses', data)).catch(error => notify(error)),
			api.call(api.list_classes, q).then((data) => db.set('classes', data)).catch(error => notify(error)),
			api.call(api.list_categories, q).then((data) => db.set('categories', data)).catch(error => notify(error)),
			api.call(api.list_programs, q).then((data) => db.set('programs', data)).catch(error => notify(error)),
			api.call(api.list_devices, q).then((data) => db.set('devices', data)).catch(error => notify(error)),
			api.call(api.list_units, q).then((data) => db.set('units', data)).catch(error => notify(error))
			]);
			await this.loadScopedLocations(this.filter);
		},
		async queryData(query)
		{
			return await api.call(api.list_stocks, query);
		},
		async loadRecord(id)
		{
			const data = await api.call(api.select_stock, ...JSON.parse(id));
			if (data && typeof data === 'object') {
				// Mapear dados enriquecidos para os campos somente leitura do modal
				data.stock_sku = data.sku ?? '';
				data.stock_product_name = data.product_name ?? '';
				data.stock_ncm = data.ncm ?? '';
				data.stock_gtin = data.gtin ?? '';
				data.stock_packaging_code = data.packaging_code ?? '';
				data.stock_packaging_factor = data.packaging_factor ?? '';
				data.stock_packaging_qr = data.packaging_qr_code ?? '';
				data.stock_lot_number = formatLotNumber(data.lot_number) ?? '';
				data.stock_expiration_date = data.expiration_date ?? '';
				data.stock_manufacture_date = data.manufacture_date ?? '';
				data.stock_program_name = data.program_name ?? '';
				data.stock_codigo_cliente = data.codigo_cliente ?? '';
				data.stock_input_created_on = data.input_created_on ? (() => { const d = new Date(data.input_created_on); return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`; })() : '';
				data.stock_input_id = data.input_id ?? '';
				data.stock_invoice_number = data.invoice_number ?? '';
				data.stock_supplier_name = data.supplier_name ?? '';
				data.stock_supplier_legal_name = data.supplier_legal_name ?? '';
				data.stock_manufacturer_nick = data.manufacturer_nick ?? '';
				data.stock_block_reason = Number(data.quantity_unsettled || 0) > 0
					? String(data.block_reason || '').trim()
					: '';
			}
			return data;
		},
		async saveRecord() {
			// Tela de estoque é somente consulta; não persiste alterações por aqui
			return false;
		},
		async editFilter() {
			// Filtrar sempre limpo — preferências de colunas ficam no Configurar.
			await this.loadScopedLocations({});
			filter.edit(this.applyFilter.bind(this), {}, {
				reloadLocations: async (formData) => {
					await this.loadScopedLocations(formData);
				}
			});
		},
		async applyFilter(data) {
			let resolved = await resolveFilterLabelsToIds(data, this.fieldspecs);
			resolved = this.normalizeFilterData(resolved);
			await this.loadScopedLocations(resolved);
			this.filter = resolved;
			this.filterExplicitlyApplied = true;
			await this.loadPage(1);
		},
		applySetup(data)
		{
			// Colunas / ordenação / page_length do Configurar — sem restaurar Filtrar.
			if (data.sort_column != null && data.sort_column !== '')
			{
				this.sort_column = Number(data.sort_column);
				this.sort_order = Number(data.sort_order ?? 1);
			}
			else
			{
				const rec = this.computeRecencySort();
				this.sort_column = rec.sort_column;
				this.sort_order = 1;
			}
			this.page_length = Number(data.page_length ?? this.page_length);
			this.filter = {};
			const _default = this.default_columns
				|| '1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25';
			const columns = (data.columns || _default).split(',').map((column) => Number(column));
			grid.setVisible(this.fieldspecs.map((field) => columns.includes(field.index) ? 1 : 0));
		},
		saveSetup(data)
		{
			const keepOn = data.keep_filter === true || data.keep_filter === 1 || data.keep_filter === '1';
			data.keep_filter = keepOn ? 1 : 0;
			delete data.filter;
			delete data.filter_applied;
			const prev = this.loadSetup() || {};
			for (const k of Object.keys(prev))
			{
				if (k.startsWith('sm_') && data[k] == null)
					data[k] = prev[k];
			}
			localStorage.setItem(this.module_name + '.setup', JSON.stringify(data));
			this.applySetup(data);
			if (keepOn)
			{
				this.filterExplicitlyApplied = true;
				this.loadPage();
			}
			else
			{
				this.filterExplicitlyApplied = false;
				const grid_node = this.target_node?.querySelector?.('#grid');
				if (grid_node)
					grid_node.innerHTML = '<p class="mtm" style="color:var(--color-muted)">Clique em <strong>Filtrar</strong> e depois <strong>Aplicar</strong> para carregar a grade (busca completa sem critérios ou refine com filtros). <strong>Operações</strong> (menu superior) só funcionam após aplicar e pedem confirmação antes de executar.</p>';
				const page = this.target_node?.querySelector?.('#page');
				if (page)
					page.innerText = '0/0';
			}
		},
		moveStock()
		{
			const crudSelf = this;
			this.smWithFilterAndConfirm('Mover estoque', 'Confirma abrir a movimentação? Selecione exatamente uma linha na grade (já filtrada) antes de preencher o formulário.', () =>
			{
				crudSelf._moveStockOpenForm();
			});
		},
		_moveStockOpenForm()
		{
			const showResult = async (success) =>
			{
				if (success)
				{
					this.loadPage();	// Show updated stocks
					fs.destroy();
				}
			}
			const sendRequest = async (data) =>
			{
				const reservedErr = manualMoveReservedError(data.source_stock, data.target_stock);
				if (reservedErr)
				{
					notify(reservedErr, 'Atenção');
					return;
				}
				const targetLocationId = await sm_resolve_target_location_id(data);
				if (!Number.isInteger(targetLocationId))
				{
					notify('Posição de destino inválida ou não encontrada. Use a lista ou digite o endereço exatamente (maiúsculas/minúsculas).', 'Atenção');
					return;
				}
				let params = {
					'source_company_id': data.source_warehouse_company_id,
					'source_warehouse_id': Number(data.source_warehouse_id),
					'source_location_id': Number(data.source_location_id),
					'source_stock': data.source_stock,
					'target_company_id': data.target_warehouse_company_id,
					'target_warehouse_id': Number(data.target_warehouse_id),
					'target_location_id': targetLocationId,
					'target_stock': data.target_stock,
					'quantity': Number(data.quantity),
					'reason': data.reason,
					'output_id': Number(data.output_id)
				};
				const runMove = () =>
				{
					ask(
						'Confirma registrar esta movimentação de estoque com os dados preenchidos?',
						'Confirmar movimentação',
						(key) =>
						{
							if (key !== 'Y')
								return;
							void api.call(api.move_stock, data.lot_company_id, data.lot_id, params).then(showResult);
						}
					);
				};
				if (data.source_stock === 'B')
				{
					const qty = Number(data.quantity);
					ask(
						formatBlockedMoveWarning([{ sku: data.stock_sku, quantity: qty }]),
						'Movimentar bloqueado',
						(key) =>
						{
							if (key !== 'Y')
								return;
							runMove();
						}
					);
					return;
				}
				runMove();
			}
			let fs = formset.create(move_stock_form, sendRequest);
			const crud = this;
			const origInit = fs.init.bind(fs);
			fs.init = async function()
			{
				origInit();
				// Preencher com estoque selecionado na grid
				const selected = await crud.getSelectedIds(['company_id', 'id']);
				if (selected?.length === 1)
				{
					const [company_id, stock_id] = selected[0];
					const stock = await api.call(api.select_stock, company_id, stock_id);
					if (stock)
					{
						fs.form.setValues({
							stock_sku: stock.sku ?? '',
							lot_company_id: stock.lot_company_id,
							lot_id: stock.lot_id,
							quantity: stock.quantity_available ?? 1,
							source_warehouse_company_id: stock.company_id,
							source_warehouse_id: stock.warehouse_id,
							source_location_id: stock.location_id
						});
						fs.form.refresh();
					}
				}
			};
			fs.init();
		},
		blockStock()
		{
			const crudSelf = this;
			this.smWithFilterAndConfirm('Bloquear estoque', 'Confirma abrir o bloqueio de estoque? Será necessário ter exatamente uma linha selecionada na grade filtrada.', () =>
			{
				crudSelf._blockStockOpenForm();
			});
		},
		_blockStockOpenForm()
		{
			const showResult = async (success) =>
			{
				if (success)
				{
					this.loadPage();
					fs.destroy();
				}
			};
			const sendRequest = async (data) =>
			{
				const qty = Number(data.quantity);
				const maxL = Number(data.qty_available_max || 0);
				if (!Number.isFinite(qty) || qty < 1)
				{
					notify('Informe uma quantidade válida.', 'Atenção');
					return;
				}
				if (maxL > 0 && qty > maxL)
				{
					notify(`Quantidade não pode exceder o disponível (${maxL}).`, 'Atenção');
					return;
				}
				const mk = String(data.movement_kind || '').trim();
				if (!mk)
				{
					notify('Selecione a Natureza do bloqueio.', 'Atenção');
					return;
				}
				const reason = reasonFromMovementKind(mk);
				const params = {
					source_company_id: data.source_warehouse_company_id,
					source_warehouse_id: Number(data.source_warehouse_id),
					source_location_id: Number(data.source_location_id),
					source_stock: 'L',
					target_company_id: data.target_warehouse_company_id,
					target_warehouse_id: Number(data.target_warehouse_id),
					target_location_id: Number(data.target_location_id),
					target_stock: 'B',
					quantity: qty,
					reason,
					movement_kind: mk,
				};
				ask(
					`Confirma bloquear ${qty} unidade(s) disponíveis nesta posição?`,
					'Confirmar bloqueio',
					(key) =>
					{
						if (key !== 'Y')
							return;
						void api.call(api.move_stock, data.lot_company_id, data.lot_id, params).then(showResult);
					}
				);
			};
			const fs = formset.create(block_stock_form, sendRequest);
			const crud = this;
			const origInit = fs.init.bind(fs);
			fs.init = async function ()
			{
				const sel = crud.getSelection();
				if (sel.length !== 1)
				{
					notify('Selecione exatamente uma linha de estoque.', 'Atenção');
					return;
				}
				// Seleção da grade: data-id é JSON [company_id, stock_id] — não é objeto { company_id, id }
				const tuple = sel[0];
				const company_id = Array.isArray(tuple) ? tuple[0] : tuple?.company_id;
				const stock_id = Array.isArray(tuple) ? tuple[1] : tuple?.id;
				if (company_id == null || stock_id == null)
				{
					notify('Seleção inválida. Marque a caixa da linha e tente de novo.', 'Atenção');
					return;
				}
				const stock = await api.call(api.select_stock, company_id, stock_id);
				if (!stock)
				{
					notify('Estoque não encontrado.', 'Erro');
					return;
				}
				const q = Number(stock.quantity_available || 0);
				if (q < 1)
				{
					notify('Não há quantidade disponível (L) para bloquear nesta linha.', 'Atenção');
					return;
				}
				origInit();
				fs.form.setValues({
					stock_sku: stock.sku ?? '',
					lot_display: `${stock.lot_company_id} · lote ${stock.lot_id}`,
					location_display: `Arm. ${stock.warehouse_id} · pos. ${stock.location_id}`,
					movement_kind: 'BLOQ',
					reason: reasonFromMovementKind('BLOQ'),
					lot_company_id: stock.lot_company_id,
					lot_id: stock.lot_id,
					quantity: q,
					qty_available_max: q,
					source_warehouse_company_id: stock.company_id,
					source_warehouse_id: stock.warehouse_id,
					source_location_id: stock.location_id,
					target_warehouse_company_id: stock.company_id,
					target_warehouse_id: stock.warehouse_id,
					target_location_id: stock.location_id,
				});
				fs.form.refresh();
				syncBlockReasonFromKind(fs);
			};
			fs.init();
		},
		async smEditLotMeta()
		{
			this.smWithFilterAndConfirm('Lote / datas / programa', 'Confirma abrir o ajuste de lote fab., datas e programa? <strong>Marque</strong> as linhas na grade (checkbox) antes de continuar.', async () =>
			{
				await this._smEditLotMetaRun();
			});
		},
		async _smEditLotMetaRun()
		{
			const rows = await sm_load_selected_stocks(this);
			if (!rows)
				return;
			if (typeof api.stock_management_lot_meta_preview !== 'function')
			{
				notify(
					'Esta versão do sistema não consegue abrir o ajuste de lote. Atualize a página; se persistir, peça o deploy do frontend.',
					'Atenção',
				);
				return;
			}
			let preview;
			try
			{
				preview = await api.call(api.stock_management_lot_meta_preview, {
					stocks: rows.map((r) => ({ stock_company_id: r.company_id, stock_id: r.id })),
				});
			}
			catch (e)
			{
				notify(e?.message || String(e), 'Atenção');
				return;
			}
			const previewRows = Array.isArray(preview?.rows) ? preview.rows : [];
			const previewGroups = Array.isArray(preview?.groups) ? preview.groups : [];
			if (!previewRows.length)
			{
				notify(
					'Não foi possível carregar as linhas selecionadas para o ajuste de lote. Feche os avisos (Continuar), atualize a página e tente de novo.',
					'Atenção',
				);
				return;
			}
			const companyIds = [...new Set(
				previewRows
					.map((r) => r.lot_company_id || r.stock_company_id)
					.filter(Boolean)
					.map((c) => String(c)),
			)];
			const programsCompanyId = companyIds.length === 1 ? companyIds[0] : null;
			const multiCompanyHint = companyIds.length > 1
				? 'Seleção com mais de uma empresa: dá para alterar lote/datas; para mudar o Programa, selecione linhas de uma única empresa.'
				: '';
			const sharedLot = previewGroups.some(
				(g) => (g.lines_with_same_lot || 0) > (g.selected_lines || 0)
					|| (g.lines_with_same_lot || 0) > 1
			);
			const crud = this;
			sm_lot_meta_form.form._programsCompanyId = programsCompanyId;
			const fs = formset.create(sm_lot_meta_form, async (data) =>
			{
				const has =
					(data.lot_number != null && String(data.lot_number).trim() !== '')
					|| !!data.manufacture_date
					|| !!data.expiration_date
					|| (data.program_id !== '' && data.program_id != null);
				if (!has)
				{
					notify('Preencha ao menos um campo a alterar.', 'Atenção');
					return;
				}
				const onlySelected = !!data.only_selected_lines;
				let programId = null;
				if (data.program_id !== '' && data.program_id != null)
				{
					if (!programsCompanyId)
					{
						notify(
							'Para alterar o programa, selecione linhas de uma única empresa (tenant).',
							'Atenção',
						);
						return;
					}
					programId = Number(data.program_id);
					if (!Number.isInteger(programId) || programId <= 0)
					{
						notify('Selecione um programa válido da lista (empresa das linhas).', 'Atenção');
						return;
					}
				}
				const meta = {
					...(data.lot_number != null && String(data.lot_number).trim() !== '' ? { lot_number: String(data.lot_number).trim() } : {}),
					...(data.manufacture_date ? { manufacture_date: data.manufacture_date } : {}),
					...(data.expiration_date ? { expiration_date: data.expiration_date } : {}),
					...(programId != null ? { program_id: programId } : {}),
				};
				const items = [];
				if (onlySelected)
				{
					for (const pr of previewRows)
					{
						items.push({
							scope: 'line',
							stock_company_id: pr.stock_company_id,
							stock_id: pr.stock_id,
							lot_company_id: pr.lot_company_id,
							lot_id: pr.lot_id,
							...meta,
						});
					}
				}
				else
				{
					const seenLots = new Set();
					for (const g of previewGroups)
					{
						const k = `${g.lot_company_id}|${g.lot_id}`;
						if (seenLots.has(k))
							continue;
						seenLots.add(k);
						items.push({
							scope: 'all',
							lot_company_id: g.lot_company_id,
							lot_id: g.lot_id,
							...meta,
						});
					}
				}
				const confirmMsg = onlySelected
					? `Confirma alterar o lote/datas/programa somente na(s) <strong>${previewRows.length}</strong> linha(s) selecionada(s)? O sistema isola automaticamente quando o mesmo SKU+lote existe em outras posições.`
					: (() =>
					{
						const parts = previewGroups.map((g) =>
						{
							const label = sm_lot_group_label(g);
							if ((g.lines_with_same_lot || 0) > 1)
								return `${label} — <strong>${g.lines_with_same_lot}</strong> palete(s) em <strong>${g.positions_with_same_lot}</strong> posição(ões)`;
							return `${label} — somente a linha selecionada`;
						});
						return `Confirma aplicar a todas as linhas com o mesmo SKU e lote fab.?<br><br>${parts.join('<br>')}`;
					})();
				ask(
					confirmMsg,
					'Confirmar gravação',
					(key) =>
					{
						if (key !== 'Y')
							return;
						void (async () =>
						{
							const ok = await api.call(api.stock_management_batch_patch_lots, { items });
							if (ok && typeof ok.updated === 'number')
							{
								const iso = ok.isolated_lines ? ` (${ok.isolated_lines} linha(s) isolada(s))` : '';
								notify(`Lotes atualizados: ${ok.updated}.${iso}`, 'Sucesso');
								crud.loadPage();
								fs.destroy();
							}
						})();
					}
				);
			});
			const orig = fs.init.bind(fs);
			fs.init = async function ()
			{
				orig();
				const rowsPreview = previewRows.map(sm_stock_line_label).join('\n');
				const groupsPreview = previewGroups.map((g) =>
				{
					const base = sm_lot_group_label(g);
					if ((g.lines_with_same_lot || 0) <= 1)
						return `${base} — única linha no sistema`;
					return `${base} — ${g.lines_with_same_lot} palete(s) / ${g.positions_with_same_lot} posição(ões); você selecionou ${g.selected_lines}`;
				}).join('\n');
				fs.form.setValues({
					selected_rows_preview: rowsPreview,
					selected_lots_preview: groupsPreview,
					only_selected_lines: sharedLot,
				});
				if (previewGroups.length === 1 && previewRows.length >= 1)
				{
					const pr = previewRows[0];
					let lot = await db.get('lots', [ pr.lot_company_id, pr.lot_id ]);
					if (!lot)
						lot = await api.call(api.select_lot, pr.lot_company_id, pr.lot_id);
					if (lot && typeof lot === 'object')
					{
						fs.form.setValues({
							lot_number: formatLotNumber(lot.lot_number) || lot.lot_number || '',
							manufacture_date: lot.manufacture_date || '',
							expiration_date: lot.expiration_date || '',
							program_id: lot.program_id != null ? String(lot.program_id) : '',
						});
						fs.form.refresh();
					}
				}
				const node = fs.dialog?.getNode?.();
				const hint = node?.querySelector('#sm-lot-hint');
				const scopeBox = node?.querySelector('#sm-lot-scope-box');
				if (scopeBox)
					scopeBox.style.display = sharedLot ? '' : 'none';
				if (hint)
				{
					const scopeHint = !sharedLot
						? 'Este SKU e lote fab. existem apenas na(s) linha(s) selecionada(s). A alteração não afeta outros paletes.'
						: 'O mesmo SKU e lote fab. podem existir em vários paletes/posições. Escolha se a alteração vale para todas ou só para as linhas marcadas.';
					hint.textContent = multiCompanyHint ? `${multiCompanyHint} ${scopeHint}` : scopeHint;
				}
			};
			fs.init();
		},
		async smChangePackaging()
		{
			this.smWithFilterAndConfirm('Corrigir embalagem', 'Confirma corrigir a embalagem dos lotes das linhas <strong>selecionadas</strong>? Todas devem ser do mesmo produto. O sistema valida múltiplo do fator e registra auditoria.', async () =>
			{
				await this._smChangePackagingRun();
			});
		},
		async _smChangePackagingRun()
		{
			const rows = await sm_load_selected_stocks(this);
			if (!rows)
				return;
			const keys = new Map();
			for (const s of rows)
			{
				const k = `${s.lot_company_id}|${s.lot_id}`;
				if (!keys.has(k))
					keys.set(k, { lot_company_id: s.lot_company_id, lot_id: s.lot_id, product_id: s.product_id });
			}
			const lots = [ ...keys.values() ];
			const pids = new Set(lots.map((L) => L.product_id));
			if (pids.size !== 1)
			{
				notify('Selecione linhas do mesmo produto (ou um único lote).', 'Atenção');
				return;
			}
			const product_id = lots[0].product_id;
			const lot_company_id = lots[0].lot_company_id;
			const ctx = sm_packaging_context(rows);
			const crud = this;
			const fs = formset.create(sm_packaging_form, async (data) =>
			{
				const pid = Number(data.product_packaging_id);
				if (!Number.isFinite(pid))
				{
					notify('Selecione a embalagem de destino.', 'Atenção');
					return;
				}
				const reason = String(data.reason || '').trim();
				const reasonErr = sm_validate_packaging_reason(reason);
				if (reasonErr)
				{
					notify(reasonErr, 'Atenção');
					return;
				}
				ask(
					`Confirma aplicar a embalagem escolhida em ${lots.length} lote(s)? O saldo deve ser múltiplo do fator e a justificativa será auditada.`,
					'Confirmar correção de embalagem',
					(key) =>
					{
						if (key !== 'Y')
							return;
						void (async () =>
						{
							let n = 0;
							let lastErr = '';
							for (const L of lots)
							{
								try
								{
									const ok = await api.call(api.stock_management_change_packaging, {
										lot_company_id: L.lot_company_id,
										lot_id: L.lot_id,
										product_packaging_id: pid,
										reason,
									});
									if (ok)
										n++;
								}
								catch (e)
								{
									lastErr = e?.message || String(e);
								}
							}
							if (n)
							{
								notify(n === lots.length
									? `Embalagem corrigida em ${n} lote(s).`
									: `Embalagem corrigida em ${n} de ${lots.length} lote(s).${lastErr ? ' ' + lastErr : ''}`,
								n === lots.length ? 'Sucesso' : 'Atenção');
								crud.loadPage();
								fs.destroy();
							}
							else if (lastErr)
								notify(lastErr, 'Erro');
						})();
					}
				);
			});
			fs._pkgList = [];
			fs._pkgTotals = { total: ctx.total };
			const updatePkgPreview = () =>
			{
				const node = fs.dialog?.getNode?.();
				const preview = node?.querySelector('#pkg_preview');
				const pid = Number(node?.querySelector('#product_packaging_id')?.value);
				if (!preview)
					return;
				if (!Number.isFinite(pid))
				{
					preview.textContent = 'Selecione a embalagem de destino para ver a prévia.';
					return;
				}
				const pkg = (fs._pkgList || []).find((p) => Number(p.id) === pid);
				if (!pkg)
				{
					preview.textContent = 'Embalagem não encontrada na lista do produto.';
					return;
				}
				const f = Number(pkg.factor);
				const total = Number(fs._pkgTotals.total) || 0;
				if (!(f > 0))
				{
					preview.textContent = 'Fator da embalagem inválido.';
					return;
				}
				if (total % f === 0)
					preview.textContent = `Prévia: ${total} un. ÷ fator ${f} = ${total / f} embalagem(ns) — compatível com o saldo total selecionado.`;
				else
					preview.textContent = `Saldo total (${total} un.) não é múltiplo do fator ${f}. A operação será rejeitada pela API.`;
			};
			const orig = fs.init.bind(fs);
			fs.init = async function ()
			{
				orig();
				const node = fs.dialog?.getNode?.();
				const s0 = ctx.first;
				const set = (sel, val) =>
				{
					const el = node?.querySelector(sel);
					if (el)
						el.value = val ?? '';
				};
				set('#pkg_product_name', s0.product_name || s0.sku || '');
				set('#pkg_lot_number', formatLotNumber(s0.lot_number) || s0.lot_number || '');
				set('#pkg_lot_id', lots.length === 1
					? String(s0.lot_id ?? '')
					: `${lots.length} lotes (${lots.map((L) => L.lot_id).join(', ')})`);
				set('#pkg_expiration', s0.expiration_date || '');
				set('#pkg_location', ctx.locs.length === 1
					? ctx.locs[0]
					: (ctx.locs.length ? ctx.locs.join(', ') : ''));
				set('#pkg_current_code', s0.packaging_code || '');
				set('#pkg_current_factor', s0.packaging_factor != null ? String(s0.packaging_factor) : '');
				set('#pkg_total', String(ctx.total));
				set('#pkg_available', String(ctx.avail));
				set('#pkg_reserved', String(ctx.res));
				set('#pkg_blocked', String(ctx.block));
				const list = await api.call(api.list_product_packagings_by_product, lot_company_id, product_id) || [];
				fs._pkgList = list.filter((p) => !p.is_disabled);
				const dl = node?.querySelector('#sm_pkg_list');
				if (dl)
				{
					dl.innerHTML = fs._pkgList.map((p) =>
						`<option value="${p.id}">${p.code} · fator ${p.factor} (${p.level})</option>`
					).join('');
				}
				const pkgInput = node?.querySelector('#product_packaging_id');
				if (pkgInput)
				{
					pkgInput.addEventListener('input', updatePkgPreview);
					pkgInput.addEventListener('change', updatePkgPreview);
				}
				updatePkgPreview();
			};
			fs.init();
		},
		async smChangeUnit()
		{
			this.smWithFilterAndConfirm('Trocar UM (produto)', 'Confirma alterar a unidade de medida do produto? Afeta o cadastro do produto (titular + SKU) das linhas <strong>selecionadas</strong> (mesmo produto).', async () =>
			{
				await this._smChangeUnitRun();
			});
		},
		async _smChangeUnitRun()
		{
			const rows = await sm_load_selected_stocks(this);
			if (!rows)
				return;
			const keys = new Set(rows.map((s) => `${s.lot_company_id}|${s.product_id}`));
			if (keys.size !== 1)
			{
				notify('Selecione linhas do mesmo titular e produto.', 'Atenção');
				return;
			}
			const lot_company_id = rows[0].lot_company_id;
			const product_id = rows[0].product_id;
			const crud = this;
			const fs = formset.create(sm_unit_form, async (data) =>
			{
				const uid = Number(data.selling_unit_id);
				if (!Number.isFinite(uid))
				{
					notify('Informe a unidade de medida.', 'Atenção');
					return;
				}
				ask(
					'Confirma alterar a unidade de medida deste produto no cadastro (titular)?',
					'Confirmar UM',
					(key) =>
					{
						if (key !== 'Y')
							return;
						void (async () =>
						{
							const ok = await api.call(api.patch_product, lot_company_id, product_id, { selling_unit_id: uid });
							if (ok !== undefined && ok !== false)
							{
								notify('Unidade de medida do produto atualizada.', 'Sucesso');
								crud.loadPage();
								fs.destroy();
							}
						})();
					}
				);
			});
			const orig = fs.init.bind(fs);
			fs.init = async function ()
			{
				orig();
				let prod = await db.get('products', [ lot_company_id, product_id ]);
				if (!prod)
					prod = await api.call(api.select_product, lot_company_id, product_id);
				if (prod && typeof prod === 'object')
				{
					const sku = prod.sku;
					const skuStr = Array.isArray(sku) ? sku.join('') : (sku ?? '');
					fs.form.setValues({
						product_sku: skuStr,
						selling_unit_id: prod.selling_unit_id != null ? String(prod.selling_unit_id) : '',
					});
					fs.form.refresh();
				}
			};
			fs.init();
		},
		async smBulkMove()
		{
			this.smWithFilterAndConfirm('Movimentar (lote)', 'Confirma abrir a movimentação em lote? Serão movidas as quantidades das linhas <strong>selecionadas</strong> conforme o tipo de saldo escolhido no formulário.', async () =>
			{
				await this._smBulkMoveRun();
			});
		},
		async _smBulkMoveRun()
		{
			const rows = await sm_load_selected_stocks(this);
			if (!rows)
				return;
			const crud = this;
			const fs = formset.create(sm_bulk_target_form, async (data) =>
			{
				const moves = [];
				const reason = String(data.reason || '').trim();
				if (!reason)
				{
					notify('Informe o motivo.', 'Atenção');
					return;
				}
				const targetLocationId = await sm_resolve_target_location_id(data);
				if (!Number.isInteger(targetLocationId))
				{
					notify('Posição de destino inválida ou não encontrada. Use a lista ou digite o endereço exatamente (maiúsculas/minúsculas).', 'Atenção');
					return;
				}
				const srcT = data.source_stock || 'L';
				const tgtT = data.target_stock || 'L';
				const reservedErr = manualMoveReservedError(srcT, tgtT);
				if (reservedErr)
				{
					notify(reservedErr, 'Atenção');
					return;
				}
				for (const s of rows)
				{
					let q = 0;
					if (srcT === 'L')
						q = Number(s.quantity_available || 0);
					else
						q = Number(s.quantity_unsettled || 0);
					if (q < 1)
						continue;
					moves.push({
						lot_company_id: s.lot_company_id,
						lot_id: s.lot_id,
						source_company_id: s.company_id,
						source_warehouse_id: s.warehouse_id,
						source_location_id: s.location_id,
						source_stock: srcT,
						target_company_id: data.target_warehouse_company_id,
						target_warehouse_id: Number(data.target_warehouse_id),
						target_location_id: targetLocationId,
						target_stock: tgtT,
						quantity: q,
						reason,
						...(String(data.movement_kind || '').trim() ? { movement_kind: String(data.movement_kind).trim() } : {}),
					});
				}
				if (!moves.length)
				{
					notify('Nenhuma linha com quantidade na origem escolhida.', 'Atenção');
					return;
				}
				const confirmMsg = srcT === 'B'
					? formatBlockedMoveWarning(moves.map((m) =>
					{
						const row = rows.find((r) => r.lot_id === m.lot_id && r.lot_company_id === m.lot_company_id);
						return { sku: row?.sku, lot_number: row?.lot_number, quantity: m.quantity };
					}))
					: `Confirma registrar ${moves.length} movimentação(ões) com os dados informados?`;
				ask(
					confirmMsg,
					srcT === 'B' ? 'Movimentar bloqueado' : 'Confirmar movimentação em lote',
					(key) =>
					{
						if (key !== 'Y')
							return;
						if (_smBulkActionInFlight)
							return;
						_smBulkActionInFlight = true;
						void (async () =>
						{
							try
							{
								const ok = await api.call(api.stock_management_bulk_move, { moves });
								if (ok?.count >= 0)
								{
									notify(`Movimentações registradas: ${ok.count}.`, 'Sucesso');
									crud.loadPage();
									fs.destroy();
								}
							}
							finally
							{
								_smBulkActionInFlight = false;
							}
						})();
					}
				);
			});
			fs.init();
		},
		async smBulkBlock()
		{
			this.smWithFilterAndConfirm('Bloquear (lote)', 'Confirma bloquear o saldo <strong>disponível</strong> de todas as linhas <strong>selecionadas</strong> na grade filtrada?', async () =>
			{
				await this._smBulkBlockRun();
			});
		},
		async _smBulkBlockRun()
		{
			const rows = await sm_load_selected_stocks(this);
			if (!rows)
				return;
			const crud = this;
			const fs = formset.create(sm_reason_form, async (data) =>
			{
				const mk = String(data.movement_kind || '').trim();
				if (!mk)
				{
					notify('Selecione a Natureza do bloqueio.', 'Atenção');
					return;
				}
				const reason = reasonFromMovementKind(mk);
				const lines = [];
				for (const s of rows)
				{
					const q = Number(s.quantity_available || 0);
					if (q < 1)
						continue;
					lines.push({
						stock_company_id: s.company_id,
						stock_id: s.id,
						quantity: q,
						reason,
						movement_kind: mk,
					});
				}
				if (!lines.length)
				{
					notify('Nenhuma linha com disponível para bloquear.', 'Atenção');
					return;
				}
				ask(
					`Confirma bloquear o disponível em ${lines.length} linha(s)?`,
					'Confirmar bloqueio em lote',
					(key) =>
					{
						if (key !== 'Y')
							return;
						if (_smBulkActionInFlight)
							return;
						_smBulkActionInFlight = true;
						void (async () =>
						{
							try
							{
								const ok = await api.call(api.stock_management_bulk_block, { lines });
								if (ok?.count >= 0)
								{
									notify(`Bloqueios registrados: ${ok.count}.`, 'Sucesso');
									crud.loadPage();
									fs.destroy();
								}
							}
							finally
							{
								_smBulkActionInFlight = false;
							}
						})();
					}
				);
			});
			const orig = fs.init.bind(fs);
			fs.init = function ()
			{
				orig();
				fs.form.setValues({ movement_kind: 'BLOQ', reason: reasonFromMovementKind('BLOQ') });
				fs.form.refresh();
				syncBlockReasonFromKind(fs);
			};
			fs.init();
		},
		async smBulkUnblock()
		{
			this.smWithFilterAndConfirm('Desbloquear (lote)', 'Confirma desbloquear o saldo <strong>bloqueado</strong> de todas as linhas <strong>selecionadas</strong> na grade filtrada?', async () =>
			{
				await this._smBulkUnblockRun();
			});
		},
		async _smBulkUnblockRun()
		{
			const rows = await sm_load_selected_stocks(this);
			if (!rows)
				return;
			const crud = this;
			const fs = formset.create(sm_reason_form, async (data) =>
			{
				const mk = String(data.movement_kind || '').trim();
				if (!mk)
				{
					notify('Selecione a Natureza do desbloqueio.', 'Atenção');
					return;
				}
				const reason = reasonFromMovementKind(mk);
				const lines = [];
				for (const s of rows)
				{
					const q = Number(s.quantity_unsettled || 0);
					if (q < 1)
						continue;
					lines.push({
						stock_company_id: s.company_id,
						stock_id: s.id,
						quantity: q,
						reason,
						movement_kind: mk,
					});
				}
				if (!lines.length)
				{
					notify('Nenhuma linha com saldo bloqueado.', 'Atenção');
					return;
				}
				ask(
					`Confirma desbloquear o saldo em ${lines.length} linha(s)?`,
					'Confirmar desbloqueio em lote',
					(key) =>
					{
						if (key !== 'Y')
							return;
						if (_smBulkActionInFlight)
							return;
						_smBulkActionInFlight = true;
						void (async () =>
						{
							try
							{
								const ok = await api.call(api.stock_management_bulk_unblock, { lines });
								if (ok?.count >= 0)
								{
									notify(`Desbloqueios registrados: ${ok.count}.`, 'Sucesso');
									crud.loadPage();
									fs.destroy();
								}
							}
							finally
							{
								_smBulkActionInFlight = false;
							}
						})();
					}
				);
			});
			const orig = fs.init.bind(fs);
			fs.init = function ()
			{
				orig();
				fs.form.setValues({ movement_kind: 'DESBLOQ', reason: reasonFromMovementKind('DESBLOQ') });
				fs.form.refresh();
				syncBlockReasonFromKind(fs);
			};
			fs.init();
		},
		async smSplit()
		{
			this.smWithFilterAndConfirm('Dividir linha', 'Confirma dividir a linha de estoque? É necessária <strong>uma</strong> linha selecionada na grade filtrada (reserva e bloqueio zerados).', async () =>
			{
				await this._smSplitRun();
			});
		},
		async _smSplitRun()
		{
			const rows = await sm_load_selected_stocks(this);
			if (!rows || rows.length !== 1)
			{
				notify('Selecione exatamente uma linha de estoque.', 'Atenção');
				return;
			}
			const s = rows[0];
			const crud = this;
			const fs = formset.create(sm_split_form, async (data) =>
			{
				const q1 = Number(data.quantity_first);
				const isolate = !!data.isolate_new_lot;
				ask(
					`Confirma dividir a linha: ${q1} unidade(s) permanecem na linha atual e o restante vira nova linha${isolate ? ' com lote independente' : ''}?`,
					'Confirmar divisão',
					(key) =>
					{
						if (key !== 'Y')
							return;
						void (async () =>
						{
							const ok = await api.call(api.stock_management_split_stock, {
								stock_company_id: s.company_id,
								stock_id: s.id,
								quantity_first: q1,
								isolate_new_lot: isolate,
							});
							if (ok?.new_stock_id)
							{
								notify(
									ok?.new_lot_id
										? `Linha dividida. Novo estoque id ${ok.new_stock_id} com lote independente ${ok.new_lot_id}.`
										: `Linha dividida. Novo estoque id ${ok.new_stock_id}.`,
									'Sucesso'
								);
								crud.loadPage();
								fs.destroy();
							}
						})();
					}
				);
			});
			const orig = fs.init.bind(fs);
			fs.init = function ()
			{
				orig();
				const expRaw = s.expiration_date;
				let expLabel = '';
				if (expRaw != null && String(expRaw).trim() !== '')
				{
					const d = new Date(expRaw);
					expLabel = Number.isNaN(d.getTime())
						? String(expRaw)
						: d.toLocaleDateString('pt-BR');
				}
				fs.form.setValues({
					product_name: (s.product_name || '').toString().trim() || '—',
					manufacturer_nick: (s.manufacturer_nick || '').toString().trim() || '—',
					program_name: (s.program_name || '').toString().trim() || '—',
					lot_number: formatLotNumber(s.lot_number) || s.lot_number || '—',
					expiration_date: expLabel || '—',
					stock_id: String(s.id ?? ''),
					lot_ref: `SKU ${s.sku ?? ''} · Lote ${formatLotNumber(s.lot_number) || s.lot_number || '—'}`,
					stock_sku: s.sku ?? '',
					qty_available: String(s.quantity_available ?? ''),
					isolate_new_lot: true,
				});
				fs.form.refresh();
			};
			fs.init();
		},
		async smUnitarize()
		{
			if (!this.smHasActiveFilter())
			{
				this.smNotifyFilterRequired();
				return;
			}
			const rows = await sm_load_selected_stocks(this);
			if (!rows || rows.length !== 1)
			{
				notify('Selecione exatamente uma linha de estoque.', 'Atenção');
				return;
			}
			const s = rows[0];
			const co = await db.get('companies', s.lot_company_id) || await db.get('companies', [ s.lot_company_id ]);
			if (!co?.pkg_unitarization_enabled)
			{
				notify('Unitarização não está habilitada na empresa titular do lote (Organização > Empresas).', 'Atenção');
				return;
			}
			const crud = this;
			ask(
				'Separar o saldo disponível em caixas fechados (múltiplo do fator primário) e sobra em unidades, em linhas distintas?',
				'Unitarizar linha',
				async (key) =>
				{
					if (key !== 'Y')
						return;
					const ok = await api.call(api.stock_management_unitarize_stock, {
						stock_company_id: s.company_id,
						stock_id: s.id,
					});
					if (ok && (ok.changed === false || ok.new_stock_id))
					{
						notify(ok.message || 'Unitarização concluída.', ok.changed === false ? 'Aviso' : 'Sucesso');
						crud.loadPage();
					}
				}
			);
		},
		reportPayload(filterData, extra = {})
		{
			const filterExpr = filterData ? translate(filterData, null, this.fieldspecs) : '';
			const sortVal = this.sort_column * (this.sort_order ? -1 : 1);
			return { filter: filterExpr || undefined, sort: sortVal, ...extra };
		},
		openReportModal(title, apiMethod)
		{
			let reportFilter = null;
			const crud = this;
			const descriptor = { ...report_modal_form };
			descriptor.html = descriptor.html.replace('<div class="title" id="report-title">Relatório</div>', `<div class="title" id="report-title">${title}</div>`);
			const fs = formset.create(descriptor, async (data) =>
			{
				fs.destroy();
				const payload = crud.reportPayload(reportFilter, { format: data.format });
				try {
					const json = await api.call(apiMethod, payload);
					if (json?.pdf) pdf(json.pdf);
					else if (json?.csv) {
						downloadCsv(json.csv, json.filename || 'relatorio.csv');
						notify('Download iniciado.', 'Sucesso');
					}
					else notify('Nenhum dado para o relatório.', 'Aviso');
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			});
			fs.init = (() =>
			{
				Object.getPrototypeOf(fs).init.call(fs);
				const node = fs.dialog?.getNode?.();
				if (node) {
					const btnFilters = node.querySelector('#btn-filters');
					const summary = node.querySelector('#filter-summary');
					const updateSummary = () => {
						summary.textContent = reportFilter ? 'Filtros definidos' : 'Sem filtros (todos os registros)';
					};
					btnFilters?.addEventListener('click', async () =>
					{
						await crud.loadScopedLocations(reportFilter || crud.filter);
						filter.edit(async (data) =>
						{
							reportFilter = await resolveFilterLabelsToIds(data, crud.fieldspecs);
							await crud.loadScopedLocations(reportFilter);
							updateSummary();
						}, reportFilter, { reloadLocations: async (formData) =>
						{
							await crud.loadScopedLocations(formData);
						}});
					});
					updateSummary();
				}
			})();
		},
		printBySKU() { this.openReportModal('Estoque Consolidado por SKU', api.print_stocks_by_sku); },
		printByLot() { this.openReportModal('Estoque Consolidado por Lote', api.print_stocks_by_lot); },
		printByLocation() { this.openReportModal('Estoque Consolidado por Posição', api.print_stocks_by_location); },
		printBalanceBlocks() { this.openReportModal('Bloqueios de saldo', api.print_stocks_balance_blocks); },
		exportCsv()
		{
			const fs = formset.create(export_csv_form, async (data) =>
			{
				fs.destroy();
				const payload = {
					company_id: data.company_id,
					warehouse_id: data.warehouse_id ? Number(data.warehouse_id) : null,
					program_id: data.program_id ? Number(data.program_id) : null
				};
				try {
					const json = await api.call(api.export_stocks_csv, payload);
					if (json?.csv) {
						downloadCsv(json.csv, json.filename || 'estoque.csv');
						notify('Download iniciado.', 'Sucesso');
					} else {
						notify('Nenhum dado para exportar.', 'Aviso');
					}
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			});
			fs.init();
		},
		outputPurchaseReport()
		{
			const fs = formset.create(output_purchase_form, async (data) =>
			{
				const companyId = data.company_id;
				if (!companyId) {
					notify('Selecione a empresa.', 'Atenção');
					return false;
				}
				const dateFrom = data.date_from;
				const dateTo   = data.date_to;
				if (!dateFrom || !dateTo) {
					notify('Informe o período (De e Até).', 'Atenção');
					return false;
				}
				if (dateTo < dateFrom) {
					notify('A data "Até" deve ser igual ou posterior à data "De".', 'Atenção');
					return false;
				}
				fs.destroy();
				try {
					const resp = await api.output_purchase_report(companyId, {
						date_from: dateFrom,
						date_to:   dateTo,
						warehouse_id: data.warehouse_id || undefined,
					});
					if (!resp.ok) {
						const err = await resp.json().catch(() => ({}));
						notify(err?.error || 'Erro ao exportar.', 'Erro');
						return;
					}
					const json = await resp.json();
					if (json?.csv) {
						downloadCsv(json.csv, json.filename || 'saidas-compras.csv');
						notify(`Download iniciado (${json.rows ?? ''} linha(s)).`, 'Sucesso');
					} else {
						notify('Nenhum dado para exportar.', 'Aviso');
					}
				} catch (e) {
					notify(e?.message || String(e), 'Erro');
				}
			});
			fs.init();
		},
		printLotLabels()
		{
			if (!this.ensureSelection('Etiquetas de Lote'))
				return;
			const sendRequest = async (data) =>
			{
				const device_id = resolvePrinterDeviceId(data.device_id, '#stocks-select-printer #device_id');
				if (device_id == null)
				{
					notify('Selecione a impressora na lista (clique na opção; só o texto no campo não basta).', 'Atenção');
					return false;
				}
				const selected = await this.getSelectedIds(['company_id', 'id']);
				if (!selected.length)
				{
					notify('Selecione ao menos um estoque na grade antes de continuar.', 'Etiquetas de Lote');
					return false;
				}
				const payload = {
					company_id: data.company_id,
					device_id,
					ids: splitIdsByCompany(selected),
					return_zpl: !!data.use_local_agent,
				};
				let lc = parseInt(String(data.label_copies ?? '1'), 10);
				if (!Number.isFinite(lc) || lc < 1) lc = 1;
				if (lc > 200) lc = 200;
				payload.label_copies = lc;
				const result = await api.call(api.print_lot_labels, payload);
				if (result?.zpl) {
					try {
						await api.sendZplToAgent(result.zpl, result.printer, result.printer_key);
						notify('Etiquetas enviadas ao agente local.', 'Impressão');
					} catch (e) {
						notify(e?.message || String(e), 'Agente local');
						return false;
					}
				}
				return result;
			}
			let fs = formset.create(select_printer_form, sendRequest);
			fs.init = (() =>
			{
				document.querySelector(`${fs.form.selector} #count`).innerHTML = this.getCount();
				Object.getPrototypeOf(fs).init.call(fs);
			})();
		},
	});
	
	// Ordem = colunas da grade (alinhado a js/views/stocks.js). Índice = posição no SELECT SQL_LIST_BY_CREDENTIAL_ENRICHED (src/repos/stocks.py). Coluna 32 extra: `warehouse_name` (API). Coluna 33: `classes`. Coluna 34: `categoria`.
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Estoque', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador do estoque' },
		{ index: 12, field: 'sku', label: 'SKU', types: [types.name()], placeholder: '', title: 'SKU do produto' },
		{ index: 13, field: 'product_name', label: 'Produto', types: [types.name()], placeholder: '', title: 'Nome/taxonomia do produto' },
		{ index: 15, field: 'manufacturer_nick', label: 'Fabricante', types: [types.name()], placeholder: '', title: 'Com embalagem no lote: fabricante GTIN da embalagem; sem embalagem cadastrada: fabricante legado do produto' },
		{ index: 16, field: 'ncm', label: 'NCM', types: [types.name()], placeholder: '', title: 'NCM do produto' },
		{ index: 17, field: 'gtin', label: 'EAN', types: [types.name()], placeholder: '', title: 'GTIN/EAN da embalagem do lote (sem GTIN na embalagem: cadastro do produto)' },
		{ index: 18, field: 'codigo_cliente', label: 'Cód. cliente', types: [types.name()], placeholder: '', title: 'Código do cliente no produto' },
		{ index: 19, field: 'packaging_code', label: 'Embalagem', types: [types.name()], placeholder: '', title: 'Código da embalagem padrão do produto' },
		{ index: 20, field: 'packaging_factor', label: 'Fator Emb.', types: [types.number(0, 2)], placeholder: '', title: 'Fator da embalagem padrão' },
		{ index: 21, field: 'packaging_qr_code', label: 'QR Emb.', types: [types.name()], placeholder: '', title: 'QR code da embalagem padrão' },
		{ index: 22, field: 'program_id', label: 'Programa', types: [types.datalist()], placeholder: '', title: 'Programa do lote', store: 'programs', labels: ['name'] },
		{ index: 24, field: 'lot_number', label: 'Lote fab.', types: [types.name()], placeholder: '', title: 'Número do lote fabricante' },
		{ index: 26, field: 'expiration_date', label: 'Validade', types: [types.date()], placeholder: '', title: 'Data de validade do lote' },
		{ index: 25, field: 'manufacture_date', label: 'Fabricação', types: [types.date()], placeholder: '', title: 'Data de fabricação do lote' },
		{ index: 28, field: 'input_created_on', label: 'Data Entrada', types: [types.date()], placeholder: '', title: 'Data da entrada referente ao lote' },
		{ index: 27, field: 'input_id', label: 'Entrada Sial', types: [types.number(0, 2)], placeholder: '', title: 'Número da entrada SIAL referente ao lote' },
		{ index: 29, field: 'invoice_number', label: 'Nota Entrada', types: [types.name()], placeholder: '', title: 'Número da nota de entrada' },
		{ index: 30, field: 'supplier_name', label: 'Fornecedor', types: [types.name()], placeholder: '', title: 'Apelido do fornecedor da entrada' },
		{ index: 39, field: 'supplier_legal_name', label: 'Fornecedor (nome)', types: [types.name()], placeholder: '', title: 'Nome / razão social do fornecedor da entrada' },
		{ index: 3, field: 'warehouse_id', label: 'Armazém', types: [types.datalist()], placeholder: '', title: 'Armazém do estoque', store: 'warehouses', labels: ['name'] },
		{ index: 4, field: 'location_id', label: 'Posição', types: [types.datalist()], placeholder: '', title: 'Posição no armazém', store: 'locations', labels: ['address'], separator: '-' },
		{ index: 5, field: 'lot_company_id', label: 'Titular', types: [types.datalist()], placeholder: '', title: 'Titular do lote', store: 'companies', labels: ['nick'] },
		{ index: 6, field: 'lot_id', label: 'Lote', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador do lote' },
		{ index: 7, field: 'quantity_available', label: 'Disponível', types: [types.number(0, 2)], placeholder: '0', title: 'Quantidade disponível' },
		{ index: 8, field: 'quantity_reserved', label: 'Reservado', types: [types.number(0, 2)], placeholder: '0', title: 'Quantidade reservada' },
		{ index: 9, field: 'quantity_unsettled', label: 'Bloqueado', types: [types.number(0, 2)], placeholder: '0', title: 'Quantidade sob análise' },
		{ index: 37, field: 'unit_price', label: 'Valor unit.', types: [types.number(4, 2)], placeholder: '0,0000', title: 'Valor unitário médio do item no pedido de entrada' },
		{ index: 38, field: 'total_value', label: 'Valor total', types: [types.number(2, 2)], placeholder: '0,00', title: 'Valor unitário x quantidade disponível' },
		{ index: 10, field: 'status', label: 'Status', types: [types.opt_stock_status(), types.datalist()], placeholder: '', title: 'Status do estoque' },
		{ index: 33, field: 'classes', label: 'Classes', types: [types.name(), types.datalist()], placeholder: '', title: 'Classes do produto (separadas por ;)', store: 'classes', labels: ['name'] },
	{ index: 34, field: 'categoria', label: 'Categoria', types: [types.name(), types.datalist()], placeholder: '', title: 'Categoria do produto', store: 'categories', labels: ['name'] },
	{ index: 35, field: 'is_portaria_344', label: 'Portaria 344', types: [types.opt_bool()], placeholder: '', title: 'Produto sujeito à Portaria 344/98' },
	];

	let operations = [
		{ hash: '#sm-edit-lot', label: 'Lote&nbsp;/&nbsp;datas&nbsp;/&nbsp;programa' },
		{ hash: '#sm-change-packaging', label: 'Corrigir&nbsp;embalagem&nbsp;do&nbsp;lote' },
		{ hash: '#sm-change-unit', label: 'Trocar&nbsp;UM&nbsp;(produto)' },
		{ hash: '#sm-bulk-move', label: 'Movimentar&nbsp;(lote)' },
		{ hash: '#sm-bulk-block', label: 'Bloquear&nbsp;(lote)' },
		{ hash: '#sm-bulk-unblock', label: 'Desbloquear&nbsp;(lote)' },
		{ hash: '#sm-split', label: 'Dividir&nbsp;linha' },
		{ hash: '#sm-unitarize', label: 'Unitarizar&nbsp;linha' },
		{ hash: '#move-stock', label: 'Mover&nbsp;(uma&nbsp;linha)' },
		{ hash: '#block-stock', label: 'Bloquear&nbsp;(uma&nbsp;linha)' },
	];

	let reports = [
		{ hash: '#print-by-sku',		label: 'Estoque&nbsp;Consolidado&nbsp;por&nbsp;SKU' },
		{ hash: '#print-by-lot',		label: 'Estoque&nbsp;Consolidado&nbsp;por&nbsp;Lote' },
		{ hash: '#print-by-location',	label: 'Estoque&nbsp;Consolidado&nbsp;por&nbsp;Posição' },
		{ hash: '#print-balance-blocks',	label: 'Bloqueios&nbsp;de&nbsp;saldo' },
		{ hash: '#export-csv',			label: 'Exportar&nbsp;CSV&nbsp;(Relatório&nbsp;Completo)' },
		{ hash: '#output-purchase',		label: 'Saídas&nbsp;para&nbsp;Compras&nbsp;(CSV)' },
		{ hash: '#lot-labels',			label: 'Etiquetas&nbsp;de&nbsp;Lotes' },
	];

	router.set('#sm-edit-lot', sm_crud.smEditLotMeta.bind(sm_crud));
	router.set('#sm-change-packaging', sm_crud.smChangePackaging.bind(sm_crud));
	router.set('#sm-change-unit', sm_crud.smChangeUnit.bind(sm_crud));
	router.set('#sm-bulk-move', sm_crud.smBulkMove.bind(sm_crud));
	router.set('#sm-bulk-block', sm_crud.smBulkBlock.bind(sm_crud));
	router.set('#sm-bulk-unblock', sm_crud.smBulkUnblock.bind(sm_crud));
	router.set('#sm-split', sm_crud.smSplit.bind(sm_crud));
	router.set('#sm-unitarize', sm_crud.smUnitarize.bind(sm_crud));
	router.set('#move-stock', 			sm_crud.moveStock.bind(sm_crud));
	router.set('#block-stock',			sm_crud.blockStock.bind(sm_crud));
	router.set('#print-by-sku',			sm_crud.printBySKU.bind(sm_crud));
	router.set('#print-by-lot',			sm_crud.printByLot.bind(sm_crud));
	router.set('#print-by-location',	sm_crud.printByLocation.bind(sm_crud));
	router.set('#print-balance-blocks',	sm_crud.printBalanceBlocks.bind(sm_crud));
	router.set('#export-csv',			sm_crud.exportCsv.bind(sm_crud));
	router.set('#output-purchase',		sm_crud.outputPurchaseReport.bind(sm_crud));
	router.set('#lot-labels',			sm_crud.printLotLabels.bind(sm_crud));

	sm_crud.run(
		'stock-management',
		'Gerenciamento de estoques',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		reports
	);

};

export default stock_management;

